import { Transition } from "@headlessui/react";
import { BellIcon, XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { Fragment } from "react";

interface InAppAlertProps {
  show: boolean;
  title?: string;
  message?: string;
  onClose?: () => void;
  className: string;
  primaryCTA?: JSX.Element;
  secondaryCTA?: JSX.Element;
  icon?: JSX.Element;
  messageClassName?: string;
}

const InAppAlert = ({
  show,
  onClose,
  title,
  message,
  primaryCTA,
  secondaryCTA,
  icon,
  className,
  messageClassName = "",
}: InAppAlertProps) => {
  return (
    <div
      aria-live="assertive"
      className="pointer-events-none absolute top-8 inset-0 flex items-end px-4 py-8 sm:py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames(
              "flex pointer-events-auto w-full h-full items-stretch min-w-sm max-w-md overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-teal-600 ring-opacity-5 z-50 min-h-[80px]",
              {
                [className]: !!className,
              }
            )}
          >
            <div className="flex items-center justify-center flex-shrink-0 bg-teal-500 w-1/5">
              {icon}
            </div>
            <div className="flex flex-col">
              <div className="flex h-full w-full p-3">
                {!!title && <p className=" font-medium text-gray-900">{title}</p>}
                {!!message && (
                  <p
                    className={classNames("mt-1", {
                      [messageClassName]: !!messageClassName,
                    })}
                  >
                    {message}
                  </p>
                )}
              </div>
              {(!!primaryCTA || !!secondaryCTA) && (
                <div className="flex justify-start items-center gap-4 ml-4 mb-3">
                  {primaryCTA}
                  {secondaryCTA}
                </div>
              )}
            </div>
            {!!onClose && (
              <div className="m-2 flex flex-shrink-0">
                <button
                  type="button"
                  className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default InAppAlert;
